"use client";

import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ExcelTableProps {
  data: string[][];
  headers?: string[];
  editable?: boolean;
  className?: string;
  onCellChange?: (row: number, col: number, value: string) => void;
  onCellClick?: (row: number, col: number, value: string) => void;
  cellClassName?: (row: number, col: number, value: string) => string | undefined;
}

export const ExcelTable: React.FC<ExcelTableProps> = ({
  data,
  headers,
  className,
  onCellClick,
  cellClassName,
}) => {
  const { t } = useTranslation();
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);

  const copyAllToClipboard = useCallback(() => {
    const csvContent = [
      ...(headers ? [headers.join("\t")] : []),
      ...data.map((row) => row.join("\t")),
    ].join("\n");

    navigator.clipboard.writeText(csvContent).catch(() => {
      const textArea = document.createElement("textarea");
      textArea.value = csvContent;
      textArea.style.cssText = "position:fixed;left:-999999px;top:-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    });

    toast.success(t("excelTable.copySuccess", { count: data.length }));
  }, [data, headers, t]);

  const exportToCSV = useCallback(() => {
    const csvContent = [
      ...(headers ? [headers.join(",")] : []),
      ...data.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tabela-danych.csv";
    a.click();
    URL.revokeObjectURL(url);

    toast.success(t("excelTable.exportSuccess"));
  }, [data, headers, t]);

  const thClass =
    "px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-center whitespace-nowrap border-b border-border bg-muted text-muted-foreground";
  const tdClass =
    "px-2 py-1.5 text-right text-xs whitespace-nowrap border-b border-border text-foreground";

  return (
    <div className={cn("w-full", className)}>
      <div className="flex justify-between items-center mb-3">
        <div />
        <div className="flex gap-2">
          <button
            onClick={copyAllToClipboard}
            className="px-3 py-1.5 text-xs bg-accent text-muted-foreground rounded-lg border border-border hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
          >
            {t("excelTable.copyAll")}
          </button>
          <button
            onClick={exportToCSV}
            className="px-3 py-1.5 text-xs bg-[#ef3e29]/20 text-[#ef3e29] rounded-lg border border-[#ef3e29]/30 hover:bg-[#ef3e29]/30 transition-colors cursor-pointer"
          >
            {t("excelTable.exportCsv")}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs border-collapse min-w-max">
          <thead>
            <tr>
              <th className={cn(thClass, "w-10")}>#</th>
              {headers?.map((header, i) => (
                <th key={i} className={thClass}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const isFirstRow = rowIndex === 0 && row[0] === "Σ";
              return (
                <tr
                  key={rowIndex}
                  className={cn(
                    "transition-colors",
                    isFirstRow
                      ? "bg-[#ef3e29]/10 font-semibold"
                      : rowIndex % 2 === 0
                        ? "bg-transparent hover:bg-muted"
                        : "bg-muted/30 hover:bg-muted"
                  )}
                >
                  <td className={cn(tdClass, "text-center text-muted-foreground w-10")}>
                    {isFirstRow ? "Σ" : rowIndex}
                  </td>
                  {row.map((cell, colIndex) => (
                    <td
                      key={colIndex}
                      title={cell}
                      className={cn(
                        tdClass,
                        selectedCell?.row === rowIndex &&
                          selectedCell?.col === colIndex &&
                          "ring-1 ring-[#ef3e29] bg-accent",
                        isFirstRow && "text-foreground font-medium",
                        cellClassName?.(rowIndex, colIndex, cell)
                      )}
                      onClick={() => {
                        setSelectedCell({ row: rowIndex, col: colIndex });
                        onCellClick?.(rowIndex, colIndex, cell);
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-[10px] text-muted-foreground flex gap-4">
        <span>
          {data.length} wierszy · {headers?.length || 0} kolumn
        </span>
        {selectedCell && (
          <span>
            Zaznaczono: wiersz {selectedCell.row + 1}, kolumna {selectedCell.col + 1}
          </span>
        )}
      </div>
    </div>
  );
};
